<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import Flatpickr from 'svelte-flatpickr'
    import { writable, type Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import L from 'leaflet'
    import { isEqual } from 'lodash-es'
    import { toUnicode } from 'punycode-esm'

    import { WDATA } from 'hydria/context'
    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { asAny, sanchor, sgoto, spath, sreplace } from 'hydria/svelte_helpers'
    import { escapeHtml } from 'hydria/utils/html'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'
    import { toDate } from 'hydria/utils/time'

    import { MAP_TILES } from 'admin/map'
    import { Breadcrumb } from 'breadcrumb'

    import Dropdown from 'hydria/components/Dropdown.svelte'
    import MiniKeyboard from 'wdom/components/MiniKeyboard.svelte'

    const LIMIT = 10
    const DELAY_MS = 1250
    type Systems = '' | 'cira' | 'opensrs' | 'webnames'

    const stagesOfLife = [
        { id: 'addPeriod', text: 'Add Period', short: 'Add' },
        { id: 'transferPeriod', text: 'Transfer Period', short: 'Transf' },
        { id: 'renewPeriod', text: 'Renew Period', short: 'Renew' },
        { id: 'autoRenewPeriod', text: 'Auto Renew Period', short: 'AutoR' },
        { id: 'redemptionPeriod', text: 'Redemption Period', short: 'Redempt' },
        { id: 'pendingDelete', text: 'Pending Delete', short: 'PDel' },
        { id: 'pendingRestore', text: 'Pending Restore', short: 'PRest' },
        { id: 'registered', text: 'Registered', short: 'Reg' },
    ]

    const GROUPS = [
        { tag: 'user', text: 'Compte' },
        { tag: 'registrant', text: 'Registrant' },
        { tag: 'admin', text: 'Admin' },
        { tag: 'billing', text: 'Billing' },
        { tag: 'tech', text: 'Tech' },
    ]

    // Route parameters
    const mapping: rt.ISimpleMapping = {
        q: 'string',
        group: 'string',
        locked: '3state',
        whois_privacy: '3state',
        has_ds_records: '3state',
        has_missing_years: 'boolean',
        has_fake_expiration: 'boolean',
        system: 'string',
        expiration_lower: 'string',
        expiration_upper: 'string',
        stage_of_life: 'string',
    }
    const mappingOrder = ['group', 'q']

    // Search options for the backend
    interface SOptions {
        q: string
        group: string
        locked: null | boolean
        whois_privacy: null | boolean
        has_ds_records: null | boolean
        has_missing_years: boolean
        has_fake_expiration: boolean
        system: Systems
        expiration_lower: string
        expiration_upper: string
        stage_of_life: string
    }
    const defaults: SOptions = {
        q: '',
        group: 'registrant',
        locked: null,
        whois_privacy: null,
        has_ds_records: null,
        has_missing_years: false,
        has_fake_expiration: false,
        system: '',
        expiration_lower: '',
        expiration_upper: '',
        stage_of_life: '',
    }

    // Search result from the server
    interface IGeoResult {
        // postal_code: string
        geo: { lat: number; lon: number }
        idnas: string[]
        user_id: string
        name: string
        org: string
    }
    interface ISearchResult {
        items: IGeoResult[]
        total_items: number
        excluded_domains: { id: string; idna: string }[]
    }

    // search options; kept in sync with the query string (bi-directional)
    let sOptions: SOptions = $state(Object.assign({}, defaults))
    // search options parsed from the querystring
    let loadedOptions: SOptions = Object.assign({}, defaults)

    // Set to true after the *first* query string load
    let queryStringLoaded = false

    // Purpose: set initial value on Flatpickr
    let expirationUpper: Date | undefined = $state()
    let expirationLower: Date | undefined = $state()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    function setQueryString(inOptions: SOptions) {
        if (!queryStringLoaded) return

        if (isEqual(inOptions, loadedOptions)) return

        const options = { ...inOptions }

        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/domains/map' : `/domains/map?${qs}`
            // If something other than skip has changed, we also want to enforce skip = 0
            sgoto(path)
        }
    }
    function cleanIncomingQueryString(options: SOptions) {
        const qs = rt.simpleMapToQs(new Map(Object.entries(options)), rt.simpleReverseMap(mapping), rt.simpleParamSorter(mappingOrder)).toString()
        if (qs != $querystring) {
            let path = qs == '' ? '/domains/map' : `/domains/map?${qs}`
            // First time the page loads, and the parameters are incorrect.
            // (e.g. "q=", "skip=0", wrong parameter order, and so on)
            console.warn('parameters are dirty, replacing history from', $querystring, 'to', qs)
            sreplace(path)
        }
    }

    function loadQueryString(qs: string | undefined) {
        if (qs == null) qs = ''
        let sMap = rt.simpleParseQs(qs, rt.simpleMap(mapping))
        loadedOptions = Object.assign({}, defaults, Object.fromEntries(sMap))

        // So we only trigger the reactivity on true change, in case we are about to replace()
        if (!isEqual(sOptions, loadedOptions)) {
            sOptions = { ...loadedOptions }
        }

        // We also want to keep the URL clean.
        if (!queryStringLoaded) {
            cleanIncomingQueryString(loadedOptions)
        }
        loadIntoForm()
        queryStringLoaded = true
        doSearch()
    }

    onMount(() => {
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadQueryString($qs ? $qs : '')
            }),
        )
    })

    let results: Writable<IGeoResult[]> = writable([])

    let updating = $state(false)
    let loading = true
    let searchId = 0
    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = { limit: LIMIT, excluded: true }
        if (sOptions.q != '') sdata.text = sOptions.q
        for (const [field, value] of Object.entries(sOptions)) {
            if (['q'].includes(field)) continue
            switch (mapping[field]) {
                case '3state':
                case 'boolean':
                    if (value != null) sdata[field] = value
                    break
                case 'string':
                    if (value != null && value != '') sdata[field] = value
                    break
            }
        }

        searchId++
        ;((localSearchId) => {
            simpleFetch('GET', '/json/domain/1/map', { data: JSON.stringify(sdata) })
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nGroups = $state(0)
    let nItems = $state(0)
    let excludedDomains: string[] = $state([])

    function circleProperties(item: IGeoResult) {
        const props = {
            radius: 3,
            stroke: true,
            color: '#3388ff',
            weight: 2, // stroke in pixels
            opacity: 0.5,
        }
        if (item.idnas.length >= 9) {
            props.radius = 7
            props.color = '#a8216b'
        } else if (item.idnas.length >= 6) {
            props.radius = 6
            props.color = '#f1184c'
        } else if (item.idnas.length >= 3) {
            props.radius = 5
            props.color = '#f36943'
        } else if (item.idnas.length >= 2) {
            props.radius = 4
            props.color = '#f7dc66'
        } else {
            props.radius = 4
            props.color = '#2e9599'
        }
        return props
    }

    function loadSearchResult(data: ISearchResult) {
        let litems: IGeoResult[] = []

        glayer.clearLayers()
        nGroups = data.total_items
        nItems = 0

        for (const item of data.items) {
            const marker = L.circleMarker({ lat: item.geo.lat, lng: item.geo.lon }, circleProperties(item))

            const ugroups: any[] = []
            const ugroupIndex = {}

            for (const idna of item.idnas) {
                if (ugroupIndex[item.user_id] == null) {
                    ugroupIndex[item.user_id] = {
                        id: item.user_id,
                        name: (item.name ? item.name : '?') + (item.org ? ` (${item.org})` : ''),
                        domains: [],
                    }
                    ugroups.push(ugroupIndex[item.user_id])
                }
                const ugroup = ugroupIndex[item.user_id]
                ugroup.domains.push(toUnicode(idna))
            }

            const lines: string[] = []
            for (const ugroup of ugroups) {
                let dlist = ugroup.domains.slice(0, 3).join(', ')
                if (ugroup.domains.length > 3) dlist += ', <span class="text-muted">' + $ngettext('et {n} autre…', 'et {n} autres…', { n: ugroup.domains.length - 3 }) + '</span>'
                lines.push(`<div><s-anchor href="/wdom-user/${ugroup.id}">${escapeHtml(ugroup.name)}</s-anchor></div><blocquote>${dlist}</blocquote>`)
            }

            marker.bindPopup(lines.join(''))

            glayer.addLayer(marker)

            nItems += item.idnas.length

            litems.push(item)
        }

        excludedDomains = data.excluded_domains

        results.set(litems)
        if (loading) loading = false
    }

    // svelte-ignore state_referenced_locally
    let text = writable(sOptions.q)

    function loadIntoForm() {
        $text = sOptions.q
        if (sOptions.expiration_lower) {
            expirationLower = toDate(sOptions.expiration_lower)
        }
        if (sOptions.expiration_upper) {
            expirationUpper = toDate(sOptions.expiration_upper)
        }
    }

    let textTimer: any = null

    function clearDelayTimer() {
        if (textTimer != null) {
            clearTimeout(textTimer)
            textTimer = null
        }
    }

    unsubs.push(
        text.subscribe((newValue) => {
            clearDelayTimer()
            if (newValue == sOptions.q) {
                return
            }
            textTimer = setTimeout(() => {
                sOptions.q = newValue
                textTimer = null
                setQueryString(sOptions)
            }, DELAY_MS)
        }),
    )

    function submit() {
        clearDelayTimer()
        sOptions.q = $text
        setQueryString(sOptions)
    }
    let mainInput: HTMLInputElement | undefined = $state()
    onMount(() => {
        if (mainInput) mainInput.focus()
    })

    const systemNames: { id: 'cira' | 'opensrs' | 'webnames'; txt: string }[] = [
        { id: 'cira', txt: 'CIRA' },
        { id: 'opensrs', txt: 'OpenSRS' },
        { id: 'webnames', txt: 'WebNames' },
    ]

    const flatOpts_lower = {
        element: '#expiration_lower',
    }
    const flatOpts_upper = {
        element: '#expiration_upper',
    }

    let map: any
    let currentTileId: any = $state()
    let glayer: any
    onMount(() => {
        map = L.map('domain-map').setView(WDATA['mapbox']['initial_center'], WDATA['mapbox']['initial_zoom'])

        setTile('light')
        glayer = L.layerGroup().addTo(map)

        // Don’t know why, but sometimes the tiles won’t load.
        // "clicking" on Light again solves it, so this will hopefully help.
        setTimeout(() => {
            setTile('light')
        }, 25)
    })

    function setTile(tileId: string) {
        if (currentTileId != null) map.removeLayer(MAP_TILES[currentTileId].layer)
        currentTileId = tileId
        map.addLayer(MAP_TILES[tileId].layer)
    }

    let showExcluded = $state(false)
    $effect(() => {
        if (showExcluded && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!showExcluded && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
    onDestroy(() => {
        if (showExcluded && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<style>
    .modal-dialog {
        --bs-modal-width: 85vw;
    }

    .form-domain-list {
        display: grid;
        gap: 0.25em;
        grid-template-columns: minmax(537px, 3fr) minmax(136px, 1fr);
        grid-template-rows: 1fr 1fr;
        grid-template-areas: 'text expmin' 'toggles expmax';
    }
    .form-domain-list.with-map {
        grid-template-rows: 1fr 1fr 1fr;
        grid-template-areas: 'text expmin' 'toggles expmax' 'group .';
    }
    @media (min-width: 1490px) {
        .form-domain-list {
            grid-template-columns: minmax(200px, 1fr) 160px 160px auto;
            grid-template-rows: 1fr;
            grid-template-areas: 'text expmin expmax toggles';
        }

        .form-domain-list.with-map {
            grid-template-columns: minmax(200px, 1fr) 160px 160px auto;
            grid-template-rows: 1fr 1fr;
            grid-template-areas: 'text expmin expmax toggles' '. . . group';
        }
    }
    .fdl-text {
        grid-area: text;
    }
    .fdl-expiration-min {
        grid-area: expmin;
    }
    .fdl-expiration-max {
        grid-area: expmax;
    }
    .fdl-toggles {
        /* white-space: nowrap; */
        grid-area: toggles;
        justify-self: end;
    }

    .fdl-grouping {
        grid-area: group;
        justify-self: end;
    }

    .content-map-wrapper {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    .content-map-wrapper > * {
        flex: 0 0 auto;
    }
    .content-map-wrapper > .card-map {
        flex: 1 0 0;
        display: flex;
        flex-direction: column;

        margin-bottom: 0;
    }
    .card-map > .card-header {
        flex: 0 0 auto;
    }
    .card-map > .card-body {
        flex: 1 0 0;
        overflow: auto;
    }

    #domain-map {
        width: 100%;
        /* height: 65vh; */
        height: 100%;
    }
    #domain-map :global(.leaflet-popup-content) {
        max-height: 20ex;
        overflow: auto;
    }
    #domain-map :global(.leaflet-popup-content blocquote) {
        margin: 0 0 0 1em;
        display: block;
        word-break: break-word;
    }
</style>

<div class="content-map-wrapper">
    <Breadcrumb
        crumbs={[
            { txt: $_('Domaines'), url: spath('/domains') },
            { icon: 'fa:map-o', txt: $_('Carte') },
        ]}
    />

    <div class="card mb-4">
        <div class="card-header text-bg-primary">{$_('Carte des domaines')}</div>
        <div class="card-body">
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    submit()
                }}
            >
                <div class="form-domain-list with-map">
                    <div class="fdl-text">
                        <div class="input-group">
                            <input type="text" class="form-control" id="domain-text" placeholder={$_('Type a domain or a name server')} bind:this={mainInput} bind:value={$text} />
                            <MiniKeyboard eid={'domain-text'} />
                            <button class="btn btn-primary search" type="submit"><Icon inline icon="fa:search" /></button>
                        </div>
                    </div>

                    <div class="fdl-expiration-min">
                        <Flatpickr
                            options={asAny(flatOpts_lower)}
                            value={expirationLower}
                            formattedValue={sOptions.expiration_lower}
                            on:valueUpdate={(e) => {
                                sOptions.expiration_lower = e.detail[1]
                                setQueryString(sOptions)
                            }}
                            dateFormat="Y-m-d"
                            element="#expiration_lower"
                        >
                            <div id="expiration_lower" class="input-group">
                                <input type="text" class="form-control" data-input placeholder="Expiration min" />
                                <button
                                    class="btn btn-outline-secondary"
                                    type="button"
                                    onclick={() => {
                                        sOptions.expiration_lower = ''
                                        setQueryString(sOptions)
                                    }}
                                    data-clear
                                    disabled={!sOptions.expiration_lower}><Icon inline icon="fa:times" /></button
                                >
                            </div>
                        </Flatpickr>
                    </div>
                    <div class="fdl-expiration-max">
                        <Flatpickr
                            options={asAny(flatOpts_upper)}
                            value={expirationUpper}
                            formattedValue={sOptions.expiration_upper}
                            on:valueUpdate={(e) => {
                                sOptions.expiration_upper = e.detail[1]
                                setQueryString(sOptions)
                            }}
                            dateFormat="Y-m-d"
                            element="#expiration_upper"
                        >
                            <div id="expiration_upper" class="input-group">
                                <input type="text" class="form-control" data-input placeholder="Expiration max" />
                                <button
                                    class="btn btn-outline-secondary"
                                    type="button"
                                    onclick={() => {
                                        sOptions.expiration_upper = ''
                                        setQueryString(sOptions)
                                    }}
                                    data-clear
                                    disabled={!sOptions.expiration_upper}><Icon inline icon="fa:times" /></button
                                >
                            </div>
                        </Flatpickr>
                    </div>

                    <div class="fdl-toggles">
                        <div class="btn-group">
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.locked === true}
                                onclick={() => {
                                    sOptions.locked = sOptions.locked === true ? null : true
                                    setQueryString(sOptions)
                                }}
                                data-tooltip={'« locked » (transfer)'}><Icon inline icon="fa:lock" /></button
                            >
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.locked === false}
                                onclick={() => {
                                    sOptions.locked = sOptions.locked === false ? null : false
                                    setQueryString(sOptions)
                                }}
                                data-tooltip={'non « locked » (transfer)'}><Icon inline icon="fa:unlock" /></button
                            >
                        </div>
                        <div class="btn-group">
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.whois_privacy === true}
                                onclick={() => {
                                    sOptions.whois_privacy = sOptions.whois_privacy === true ? null : true
                                    setQueryString(sOptions)
                                }}
                                data-tooltip={'avec « Whois Privacy »'}><Icon inline icon="fa:eye-slash" /></button
                            >
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.whois_privacy === false}
                                onclick={() => {
                                    sOptions.whois_privacy = sOptions.whois_privacy === false ? null : false
                                    setQueryString(sOptions)
                                }}
                                data-tooltip={'sans « Whois Privacy »'}><Icon inline icon="fa:eye" /></button
                            >
                        </div>
                        <div class="btn-group">
                            {#each systemNames as sys}
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary"
                                    disabled={updating}
                                    class:active={sOptions.system === sys.id}
                                    onclick={() => {
                                        sOptions.system = sOptions.system === sys.id ? '' : sys.id
                                        setQueryString(sOptions)
                                    }}>{sys.txt}</button
                                >
                            {/each}
                        </div>
                        <div class="btn-group">
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.has_ds_records === true}
                                onclick={() => {
                                    sOptions.has_ds_records = sOptions.has_ds_records === true ? null : true
                                    setQueryString(sOptions)
                                }}
                                data-tooltip={'DNSSEC activé'}>DS</button
                            >
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.has_ds_records === false}
                                onclick={() => {
                                    sOptions.has_ds_records = sOptions.has_ds_records === false ? null : false
                                    setQueryString(sOptions)
                                }}
                                data-tooltip={'DNSSEC désactivé'}
                                style="text-decoration: line-through;">DS</button
                            >
                        </div>
                        <div class="btn-group">
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.has_missing_years === true}
                                onclick={() => {
                                    sOptions.has_missing_years = !sOptions.has_missing_years
                                    setQueryString(sOptions)
                                }}
                                data-tooltip="Années non-activées"><Icon inline icon="mdi:calendar-plus-outline" /></button
                            >
                        </div>
                        <div class="btn-group">
                            <button
                                type="button"
                                class="btn btn-outline-secondary"
                                disabled={updating}
                                class:active={sOptions.has_fake_expiration === true}
                                onclick={() => {
                                    sOptions.has_fake_expiration = !sOptions.has_fake_expiration
                                    setQueryString(sOptions)
                                }}
                                data-tooltip="Expiration temporaire"><Icon inline icon="mdi:calendar-end-outline" /></button
                            >
                        </div>

                        <div class="btn-group">
                            <Dropdown
                                items={stagesOfLife}
                                placeholder="SOL"
                                onSelect={(e) => {
                                    sOptions.stage_of_life = e
                                    setQueryString(sOptions)
                                }}
                                text={'text'}
                                selectedText={'short'}
                                id={'id'}
                                selected={sOptions.stage_of_life}
                                empty={''}
                                clear={'Tout'}
                                disabled={updating}
                                alignRight={true}
                                block={true}
                            />
                        </div>
                    </div>

                    <div class="fdl-grouping">
                        <div class="btn-group">
                            {#each GROUPS as { tag, text }}
                                <button
                                    type="button"
                                    class="btn btn-outline-secondary"
                                    class:active={sOptions.group === tag}
                                    onclick={() => {
                                        sOptions.group = tag
                                        setQueryString(sOptions)
                                    }}>{text}</button
                                >
                            {/each}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>

    <div class="card card-map">
        <div class="card-header text-bg-primary d-flex gap-2 align-items-center">
            <div>{$ngettext('{n} domaine', '{n} domaines', { n: nItems })} / {$ngettext('{n} groupe', '{n} groupes', { n: nGroups })}</div>
            {#if excludedDomains && excludedDomains.length}
                <div><button type="button" class="btn btn-sm btn-light" onclick={() => (showExcluded = true)}>{$ngettext('{n} domaine exclu', '{n} domaines exclus', { n: excludedDomains.length })}</button></div>
            {/if}
            <div class="col"></div>
            <div class="btn-group btn-sm">
                <button disabled type="button" class="btn btn-sm btn-light">Mapbox :</button>
                {#each Object.entries(MAP_TILES) as [id, tile]}
                    <button type="button" class="btn btn-sm btn-light" disabled={updating} class:active={id === currentTileId} onclick={() => setTile(id)}>{tile.name}</button>
                {/each}
            </div>
        </div>
        <div class="card-body">
            <div id="domain-map"></div>
        </div>
    </div>
</div>

{#if showExcluded}
    <div class="modal excluded-domain-list" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Domaines')}</div>
                </div>
                <div class="modal-body">
                    <p>Un domaine est exclu si le contact sélectionné est hors Canada, ou si le code postal nous est inconnu.</p>
                    <ul class="domain-list" style="columns: 15rem;">
                        {#each excludedDomains as edomain}
                            <li>
                                <a use:sanchor href={spath(`/domain/${edomain}`)}>{toUnicode(edomain)}</a>
                            </li>
                        {/each}
                    </ul>
                </div>
                <div class="modal-footer text-bg-light">
                    <button type="button" class="btn btn-outline-secondary" onclick={() => (showExcluded = false)}>{$_('Close')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (showExcluded = false)} aria-hidden="true"></div>
    </div>
{/if}
